import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'

import { getOperatorRfq, updateOperatorRfq } from '../api/operator'
import { rfqDisplayLabel } from '../api/rfqLabels'
import type { RfqDto } from '../api/types'
import { ApiError } from '../api/client'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { SelectCombobox } from '../components/SelectCombobox'
import { operatorRestaurantRfqsPath } from '../operator/paths'

const RFQ_STATUSES = ['open', 'draft', 'closed', 'cancelled'] as const

const formatLine = (line: RfqDto['lines'][number]): string => {
  const title = line.productName?.trim() || line.supplierSku?.trim() || `Позиция #${line.id}`
  return `${title} — ${line.quantity} ${line.unit}`
}

export const OperatorRfqScreen = () => {
  const { rfqId: rfqIdParam } = useParams()
  const rfqId = Number(rfqIdParam)
  const [rfq, setRfq] = useState<RfqDto | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState('open')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [deliveryTimeUntil, setDeliveryTimeUntil] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isFinite(rfqId) || rfqId <= 0) {
      setError('Некорректная заявка')
      setLoading(false)
      return
    }
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const loaded = await getOperatorRfq(rfqId)
        setRfq(loaded)
        setTitle(loaded.title)
        setDescription(loaded.description ?? '')
        setStatus(loaded.status)
        setDeliveryDate(loaded.desiredDeliveryDate ?? '')
        setDeliveryTimeUntil(loaded.desiredDeliveryTimeUntil?.slice(0, 5) ?? '')
        setDeliveryAddress(loaded.deliveryAddress ?? '')
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить заявку')
      } finally {
        setLoading(false)
      }
    })()
  }, [rfqId])

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await updateOperatorRfq(rfqId, {
        title,
        description,
        status,
        desiredDeliveryDate: deliveryDate || null,
        desiredDeliveryTimeUntil: deliveryTimeUntil || null,
        deliveryAddress,
      })
      setRfq(updated)
      setMessage('Заявка сохранена')
    } catch (saveError) {
      setError(saveError instanceof ApiError ? saveError.message : 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="market-page">
      <MarketPageHeader
        title={rfq ? rfqDisplayLabel(rfq) : 'Заявка'}
        description="Редактирование заявки оператором"
        actions={
          rfq ? (
            <Link
              to={operatorRestaurantRfqsPath(rfq.organizationId)}
              className="echat-btn echat-btn--ghost"
            >
              ← К списку
            </Link>
          ) : undefined
        }
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {message ? <p className="echat-muted">{message}</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && rfq ? (
        <div className="market-settings-grid market-settings-grid--single">
          <form className="echat-panel market-form" onSubmit={(event) => void handleSubmit(event)}>
            <label className="echat-field">
              <span className="echat-field__label">Название</span>
              <input
                className="echat-field__input"
                required
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>

            <label className="echat-field">
              <span className="echat-field__label">Описание</span>
              <textarea
                className="echat-field__input"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </label>

            <SelectCombobox
              label="Статус"
              value={status}
              options={RFQ_STATUSES.map((option) => ({ value: option, label: option }))}
              onChange={setStatus}
            />

            <div className="market-settings-fields-row">
              <label className="echat-field">
                <span className="echat-field__label">Дата доставки</span>
                <input
                  className="echat-field__input"
                  type="date"
                  value={deliveryDate}
                  onChange={(event) => setDeliveryDate(event.target.value)}
                />
              </label>
              <label className="echat-field">
                <span className="echat-field__label">Время до</span>
                <input
                  className="echat-field__input"
                  type="time"
                  value={deliveryTimeUntil}
                  onChange={(event) => setDeliveryTimeUntil(event.target.value)}
                />
              </label>
            </div>

            <label className="echat-field">
              <span className="echat-field__label">Адрес доставки</span>
              <input
                className="echat-field__input"
                value={deliveryAddress}
                onChange={(event) => setDeliveryAddress(event.target.value)}
              />
            </label>

            <button type="submit" className="echat-btn echat-btn--primary" disabled={saving}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </form>

          <article className="echat-panel">
            <h2 className="echat-panel__subtitle">Позиции</h2>
            {rfq.lines.length === 0 ? (
              <p className="echat-muted">Позиций нет</p>
            ) : (
              <ul className="echat-inbox__list">
                {rfq.lines.map((line) => (
                  <li key={line.id} className="echat-panel__text">
                    {formatLine(line)}
                  </li>
                ))}
              </ul>
            )}
          </article>
        </div>
      ) : null}
    </section>
  )
}
